import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { FiCalendar, FiFileText, FiPaperclip, FiPlus, FiSearch, FiStar } from "react-icons/fi";
import { RiCheckLine, RiStarFill } from "react-icons/ri";
import { ProgressBarLink } from "@/components/progress-bar";
import { Badge, Button, Input } from "@/components/ui";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Tooltip from "@/components/ui/tooltip";
import { useUser } from "@/contexts/user-provider";
import { useNoteTemplates, useTemplateDetail } from "@/hooks/use-selected-note";
import { formatDate } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";
import { QuestionType, ScribeAttachments } from "@/types/forms/question";
import { NoteTemplateTableItem } from "@/types/notes";

// Helper to count placeholders from SCRIBE_ATTACHMENTS questions
const getPlaceholderCount = (template: NoteTemplateTableItem): number => {
  if (!template.questions) return 0;
  return template.questions.reduce((count, question) => {
    if (question.questionType === QuestionType.SCRIBE_ATTACHMENTS) {
      const data = question.data as ScribeAttachments;
      return count + (data?.placeholders?.length || 0);
    }
    return count;
  }, 0);
};
const TemplateRow = t0 => {
  const $ = _c(62);
  const {
    template,
    isSelected,
    isCurrent,
    hasAccess,
    onSelect,
    onToggleFavorite
  } = t0;
  const attachmentCount = getPlaceholderCount(template);
  const questionCount = template.questions?.length ?? 0;
  const t1 = !hasAccess && "opacity-50";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("transition-all duration-200 hover:bg-hover", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = hasAccess ? "cursor-pointer" : "cursor-not-allowed";
  let t4;
  if ($[2] !== t3) {
    t4 = cx("flex items-center justify-between p-3", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground";
  let t6;
  if ($[4] !== t5) {
    t6 = cx("flex h-8 w-8 items-center justify-center rounded-md", t5);
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== isSelected) {
    t7 = isSelected ? <RiCheckLine className="h-4 w-4" /> : <FiFileText className="h-4 w-4" />;
    $[6] = isSelected;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = <div className={t6}>{t7}</div>;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== template.name) {
    t9 = <h4 className="min-w-0 break-words text-body-strong text-foreground">{template.name}</h4>;
    $[11] = template.name;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== isCurrent) {
    t10 = isCurrent && <Badge variant="neutral" className="text-caption">Current</Badge>;
    $[13] = isCurrent;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== attachmentCount) {
    t11 = attachmentCount > 0 && <Badge variant="neutral" className="px-1.5 py-0.5 text-caption"><FiPaperclip className="mr-1 h-2.5 w-2.5" />{attachmentCount}</Badge>;
    $[15] = attachmentCount;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t10 || $[18] !== t11) {
    t12 = <div className="flex shrink-0 items-center gap-1">{t10}{t11}</div>;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t12 || $[21] !== t9) {
    t13 = <div className="flex items-center gap-2">{t9}{t12}</div>;
    $[20] = t12;
    $[21] = t9;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const t14 = questionCount !== 1 ? "s" : "";
  let t15;
  if ($[23] !== t14) {
    t15 = <span>{t14}</span>;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="mx-1">•</span>;
    t17 = <span>Created</span>;
    $[25] = t16;
    $[26] = t17;
  } else {
    t16 = $[25];
    t17 = $[26];
  }
  let t18;
  if ($[27] !== template.createdDate) {
    t18 = formatDate(template.createdDate);
    $[27] = template.createdDate;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== questionCount || $[30] !== t15 || $[31] !== t18) {
    t19 = <p className="mt-0.5 truncate text-caption text-foreground-muted">{questionCount} question{t15}{t16}{t17} {t18}</p>;
    $[29] = questionCount;
    $[30] = t15;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t13 || $[34] !== t19) {
    t20 = <div className="min-w-0 flex-1">{t13}{t19}</div>;
    $[33] = t13;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t20 || $[37] !== t8) {
    t21 = <div className="flex min-w-0 flex-1 items-center gap-3">{t8}{t20}</div>;
    $[36] = t20;
    $[37] = t8;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  const t22 = !hasAccess ? "You don't have access to this template" : template.isFavorited ? "Remove from favorites" : "Add to favorites";
  let t23;
  if ($[39] !== hasAccess || $[40] !== onToggleFavorite) {
    t23 = e => {
      e.stopPropagation();
      if (hasAccess) {
        onToggleFavorite();
      }
    };
    $[39] = hasAccess;
    $[40] = onToggleFavorite;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  const t24 = !hasAccess;
  const t25 = !hasAccess ? "cursor-not-allowed text-foreground-muted/50" : template.isFavorited ? "text-yellow-500 hover:text-yellow-600 dark:text-yellow-400 dark:hover:text-yellow-300" : "text-foreground-muted hover:bg-hover hover:text-foreground-secondary";
  let t26;
  if ($[42] !== t25) {
    t26 = cx("flex h-6 w-6 items-center justify-center rounded transition-colors", t25);
    $[42] = t25;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] !== template.isFavorited) {
    t27 = template.isFavorited ? <RiStarFill className="h-4 w-4" /> : <FiStar className="h-4 w-4" />;
    $[44] = template.isFavorited;
    $[45] = t27;
  } else {
    t27 = $[45];
  }
  let t28;
  if ($[46] !== t23 || $[47] !== t24 || $[48] !== t26 || $[49] !== t27) {
    t28 = <button onClick={t23} disabled={t24} className={t26}>{t27}</button>;
    $[46] = t23;
    $[47] = t24;
    $[48] = t26;
    $[49] = t27;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] !== t22 || $[52] !== t28) {
    t29 = <div className="flex items-center gap-2"><Tooltip content={t22} side="top">{t28}</Tooltip></div>;
    $[51] = t22;
    $[52] = t28;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] !== onSelect || $[55] !== t21 || $[56] !== t29 || $[57] !== t4) {
    t30 = <div className={t4} onClick={onSelect}>{t21}{t29}</div>;
    $[54] = onSelect;
    $[55] = t21;
    $[56] = t29;
    $[57] = t4;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== t2 || $[60] !== t30) {
    t31 = <div className={t2}>{t30}</div>;
    $[59] = t2;
    $[60] = t30;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  return t31;
};
const TemplateList = t0 => {
  const $ = _c(15);
  const {
    templates,
    selectedTemplateId,
    currentTemplateId,
    hasAccessToTemplate,
    onSelect,
    onToggleFavorite
  } = t0;
  let t1;
  if ($[0] !== currentTemplateId || $[1] !== hasAccessToTemplate || $[2] !== onSelect || $[3] !== onToggleFavorite || $[4] !== selectedTemplateId || $[5] !== templates) {
    let t2;
    if ($[7] !== currentTemplateId || $[8] !== hasAccessToTemplate || $[9] !== onSelect || $[10] !== onToggleFavorite || $[11] !== selectedTemplateId) {
      t2 = template => <TemplateRow key={template.id} template={template} isSelected={selectedTemplateId === template.id} isCurrent={template.id === currentTemplateId} hasAccess={hasAccessToTemplate(template)} onSelect={() => {
        if (!hasAccessToTemplate(template)) {
          return;
        }
        if (selectedTemplateId === template.id) {
          onSelect(undefined);
        } else {
          onSelect(template.id);
        }
      }} onToggleFavorite={() => onToggleFavorite(template.id)} />;
      $[7] = currentTemplateId;
      $[8] = hasAccessToTemplate;
      $[9] = onSelect;
      $[10] = onToggleFavorite;
      $[11] = selectedTemplateId;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    t1 = templates.map(t2);
    $[0] = currentTemplateId;
    $[1] = hasAccessToTemplate;
    $[2] = onSelect;
    $[3] = onToggleFavorite;
    $[4] = selectedTemplateId;
    $[5] = templates;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[13] !== t1) {
    t2 = <div className="rounded-md border border-border divide-y divide-border overflow-hidden">{t1}</div>;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
};
const SelectTemplateModal = t0 => {
  const $ = _c(96);
  const {
    isOpen,
    setIsOpen,
    onTemplateSelected,
    onConfirm,
    onCancel,
    description: t1,
    closable: t2,
    required: t3,
    currentTemplateId,
    confirmButtonText,
    needsPlaceholderUrls: t4,
    suggestedTemplateIds
  } = t0;
  const description = t1 === undefined ? "Choose a template for your AI Scribe to follow." : t1;
  const closable = t2 === undefined ? false : t2;
  const required = t3 === undefined ? false : t3;
  const needsPlaceholderUrls = t4 === undefined ? true : t4;
  const [templateId, setTemplateId] = useState(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const {
    templates: noteTemplates,
    isPending: isLoadingTemplates,
    toggleFavorite,
    favoriteError
  } = useNoteTemplates();
  const {
    currentUser
  } = useUser();
  let t5;
  if ($[0] !== currentUser) {
    t5 = hasPermission(currentUser, PERMISSIONS.SETTINGS_NOTE_TEMPLATES_WRITE);
    $[0] = currentUser;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const canCreateTemplate = t5;
  const {
    templateDetail,
    isFetching: isLoadingSelectedTemplate
  } = useTemplateDetail(needsPlaceholderUrls ? templateId : undefined);
  let t6;
  if ($[2] !== needsPlaceholderUrls || $[3] !== noteTemplates || $[4] !== templateDetail || $[5] !== templateId) {
    t6 = needsPlaceholderUrls ? templateDetail : noteTemplates.find(t => t.id === templateId);
    $[2] = needsPlaceholderUrls;
    $[3] = noteTemplates;
    $[4] = templateDetail;
    $[5] = templateId;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const selectedTemplate = t6;
  const didConfirmRef = useRef(false);
  const {
    toast
  } = useToast();
  let t7;
  if ($[7] !== currentUser) {
    t7 = template => {
      if (!template.userIds) {
        return true;
      }
      return currentUser ? template.userIds.includes(currentUser.id) : false;
    };
    $[7] = currentUser;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const hasAccessToTemplate = t7;
  let t8;
  let t9;
  if ($[9] !== favoriteError || $[10] !== toast) {
    t8 = () => {
      if (favoriteError) {
        toast({
          title: "Unable to favorite template",
          description: "You may not have access to this template.",
          variant: "error"
        });
      }
    };
    t9 = [favoriteError, toast];
    $[9] = favoriteError;
    $[10] = toast;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  useEffect(t8, t9);
  let t10;
  bb0: {
    if (!searchTerm.trim()) {
      t10 = noteTemplates;
      break bb0;
    }
    let t11;
    if ($[13] !== noteTemplates || $[14] !== searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      t11 = noteTemplates.filter(template_0 => template_0.name.toLowerCase().includes(searchLower) || template_0.questions?.some(q => (q.data as {
        name?: string;
      })?.name?.toLowerCase().includes(searchLower)));
      $[13] = noteTemplates;
      $[14] = searchTerm;
      $[15] = t11;
    } else {
      t11 = $[15];
    }
    t10 = t11;
  }
  const filteredTemplates = t10;
  let t11;
  if ($[16] !== suggestedTemplateIds) {
    t11 = suggestedTemplateIds ?? [];
    $[16] = suggestedTemplateIds;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11) {
    t12 = new Set(t11);
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const suggestedIds = t12;
  let t13;
  if ($[20] !== suggestedTemplateIds) {
    t13 = suggestedTemplateIds ?? [];
    $[20] = suggestedTemplateIds;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== filteredTemplates || $[23] !== t13) {
    let t15;
    if ($[25] !== filteredTemplates) {
      t15 = id => filteredTemplates.filter(t_0 => t_0.id === id);
      $[25] = filteredTemplates;
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    t14 = t13.flatMap(t15);
    $[22] = filteredTemplates;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const suggested = t14;
  let t15;
  let t16;
  let t17;
  if ($[27] !== filteredTemplates || $[28] !== suggested || $[29] !== suggestedIds) {
    let t18;
    if ($[33] !== suggestedIds) {
      t18 = t_1 => !suggestedIds.has(t_1.id);
      $[33] = suggestedIds;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    const rest = filteredTemplates.filter(t18);
    t15 = suggested;
    t16 = rest.filter(_temp);
    t17 = rest.filter(_temp2);
    $[27] = filteredTemplates;
    $[28] = suggested;
    $[29] = suggestedIds;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
  } else {
    t15 = $[30];
    t16 = $[31];
    t17 = $[32];
  }
  let t18;
  if ($[35] !== t15 || $[36] !== t16 || $[37] !== t17) {
    t18 = {
      suggestedTemplates: t15,
      favoriteTemplates: t16,
      otherTemplates: t17
    };
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const {
    suggestedTemplates,
    favoriteTemplates,
    otherTemplates
  } = t18;
  let t19;
  if ($[39] !== onConfirm || $[40] !== onTemplateSelected || $[41] !== selectedTemplate || $[42] !== setIsOpen) {
    t19 = () => {
      if (selectedTemplate) {
        didConfirmRef.current = true;
        if (onConfirm) {
          onConfirm(selectedTemplate);
        }
        if (onTemplateSelected) {
          onTemplateSelected(selectedTemplate);
        }
        setIsOpen(false);
      }
    };
    $[39] = onConfirm;
    $[40] = onTemplateSelected;
    $[41] = selectedTemplate;
    $[42] = setIsOpen;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  const handleConfirm = t19;
  let t20;
  if ($[44] !== onCancel || $[45] !== required || $[46] !== setIsOpen) {
    t20 = open => {
      if (!open) {
        if (didConfirmRef.current) {
          didConfirmRef.current = false;
        } else {
          if (required) {
            return;
          }
          setTemplateId(undefined);
          setSearchTerm("");
          onCancel?.();
        }
      }
      setIsOpen(open);
    };
    $[44] = onCancel;
    $[45] = required;
    $[46] = setIsOpen;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <DialogTitle>Select Note Template</DialogTitle>;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== description) {
    t22 = <DialogHeader>{t21}<DialogDescription>{description}</DialogDescription></DialogHeader>;
    $[49] = description;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = e => setSearchTerm(e.target.value);
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== searchTerm) {
    t25 = <div className="mb-4"><div className="relative">{t23}<Input placeholder="Search templates..." value={searchTerm} onChange={t24} className="pl-10" /></div></div>;
    $[53] = searchTerm;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== currentTemplateId) {
    t26 = currentTemplateId && <Alert variant="warning" className="mb-4"><AlertDescription>Any unsaved edits to the current note will be lost. The new note will be regenerated from the original transcript.</AlertDescription></Alert>;
    $[55] = currentTemplateId;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== currentTemplateId || $[58] !== favoriteTemplates || $[59] !== filteredTemplates.length || $[60] !== hasAccessToTemplate || $[61] !== isLoadingTemplates || $[62] !== otherTemplates || $[63] !== searchTerm || $[64] !== suggestedTemplates || $[65] !== templateId || $[66] !== toggleFavorite) {
    t27 = <div className="max-h-[400px] overflow-y-auto">{isLoadingTemplates ? <div className="space-y-2">{Array.from({
          length: 3
        }).map(_temp3)}</div> : filteredTemplates.length > 0 ? <div className="space-y-4">{suggestedTemplates.length > 0 && <div><div className="mb-2 flex items-center justify-between"><div className="flex items-center gap-2"><FiCalendar className="h-3.5 w-3.5 text-foreground-secondary" /><h3 className="text-body-strong">This Appointment's Services</h3></div><span className="text-caption text-muted-foreground">{suggestedTemplates.length} template<span>{suggestedTemplates.length !== 1 ? "s" : ""}</span></span></div><TemplateList templates={suggestedTemplates} selectedTemplateId={templateId} currentTemplateId={currentTemplateId} hasAccessToTemplate={hasAccessToTemplate} onSelect={setTemplateId} onToggleFavorite={toggleFavorite} /></div>}{favoriteTemplates.length > 0 && <div><div className="mb-2 flex items-center justify-between"><div className="flex items-center gap-2"><RiStarFill className="h-3.5 w-3.5 text-yellow-500 dark:text-yellow-400" /><h3 className="text-body-strong">Favorites</h3></div><span className="text-caption text-muted-foreground">{favoriteTemplates.length} template<span>{favoriteTemplates.length !== 1 ? "s" : ""}</span></span></div><TemplateList templates={favoriteTemplates} selectedTemplateId={templateId} currentTemplateId={currentTemplateId} hasAccessToTemplate={hasAccessToTemplate} onSelect={setTemplateId} onToggleFavorite={toggleFavorite} /></div>}{otherTemplates.length > 0 && <div><div className="mb-2 flex items-center justify-between"><h3 className="text-body-strong">{searchTerm ? "Search Results" : favoriteTemplates.length > 0 || suggestedTemplates.length > 0 ? "Other Templates" : "Your Templates"}</h3><span className="text-caption text-muted-foreground">{otherTemplates.length} template<span>{otherTemplates.length !== 1 ? "s" : ""}</span></span></div><TemplateList templates={otherTemplates} selectedTemplateId={templateId} currentTemplateId={currentTemplateId} hasAccessToTemplate={hasAccessToTemplate} onSelect={setTemplateId} onToggleFavorite={toggleFavorite} /></div>}</div> : searchTerm ? <div className="flex flex-col items-center justify-center py-8 text-center"><FiSearch className="mb-3 h-8 w-8 text-foreground-muted" /><p className="mb-1 text-body text-muted-foreground">No templates found</p><p className="text-caption text-foreground-muted">Try adjusting your search terms</p></div> : <div className="flex flex-col items-center justify-center py-8 text-center"><FiFileText className="mb-3 h-8 w-8 text-foreground-muted" /><p className="mb-1 text-body text-muted-foreground">No templates available</p><p className="text-caption text-foreground-muted">Create your first template to get started</p></div>}</div>;
    $[57] = currentTemplateId;
    $[58] = favoriteTemplates;
    $[59] = filteredTemplates.length;
    $[60] = hasAccessToTemplate;
    $[61] = isLoadingTemplates;
    $[62] = otherTemplates;
    $[63] = searchTerm;
    $[64] = suggestedTemplates;
    $[65] = templateId;
    $[66] = toggleFavorite;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== t25 || $[69] !== t26 || $[70] !== t27) {
    t28 = <DialogBody>{t25}{t26}{t27}</DialogBody>;
    $[68] = t25;
    $[69] = t26;
    $[70] = t27;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  let t29;
  if ($[72] !== canCreateTemplate) {
    t29 = canCreateTemplate ? <ProgressBarLink href="/settings/note-templates" newWindow={true}><Button variant="secondary" className="w-full sm:w-auto" icon={FiPlus}>Create New Template</Button></ProgressBarLink> : <div className="hidden sm:block" />;
    $[72] = canCreateTemplate;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] !== closable || $[75] !== setIsOpen) {
    t30 = closable && <Button variant="secondary" className="w-full sm:w-auto" onClick={() => {
      setTemplateId(undefined);
      setIsOpen(false);
    }}>Cancel</Button>;
    $[74] = closable;
    $[75] = setIsOpen;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  const t31 = !templateId || isLoadingTemplates;
  const t32 = needsPlaceholderUrls && !!templateId && isLoadingSelectedTemplate;
  const t33 = confirmButtonText || "Use Template";
  let t34;
  if ($[77] !== handleConfirm || $[78] !== t31 || $[79] !== t32 || $[80] !== t33) {
    t34 = <Button variant="primary" className="w-full sm:w-auto" onClick={handleConfirm} disabled={t31} isLoading={t32}>{t33}</Button>;
    $[77] = handleConfirm;
    $[78] = t31;
    $[79] = t32;
    $[80] = t33;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== t30 || $[83] !== t34) {
    t35 = <div className="flex flex-col gap-2 sm:flex-row">{t30}{t34}</div>;
    $[82] = t30;
    $[83] = t34;
    $[84] = t35;
  } else {
    t35 = $[84];
  }
  let t36;
  if ($[85] !== t29 || $[86] !== t35) {
    t36 = <DialogFooter><div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-between">{t29}{t35}</div></DialogFooter>;
    $[85] = t29;
    $[86] = t35;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  let t37;
  if ($[88] !== t22 || $[89] !== t28 || $[90] !== t36) {
    t37 = <DialogContent className="sm:max-w-2xl">{t22}{t28}{t36}</DialogContent>;
    $[88] = t22;
    $[89] = t28;
    $[90] = t36;
    $[91] = t37;
  } else {
    t37 = $[91];
  }
  let t38;
  if ($[92] !== isOpen || $[93] !== t20 || $[94] !== t37) {
    t38 = <Dialog open={isOpen} onOpenChange={t20}>{t37}</Dialog>;
    $[92] = isOpen;
    $[93] = t20;
    $[94] = t37;
    $[95] = t38;
  } else {
    t38 = $[95];
  }
  return t38;
};
export default SelectTemplateModal;
function _temp(t_2) {
  return t_2.isFavorited;
}
function _temp2(t_3) {
  return !t_3.isFavorited;
}
function _temp3(_, index) {
  return <div key={index} className="animate-pulse rounded-md border p-3"><div className="flex items-center justify-between"><div className="flex items-center gap-2"><div className="size-4 shrink-0 rounded bg-muted" /><div className="h-4 w-32 rounded bg-muted" /></div><div className="h-4 w-16 rounded bg-muted" /></div><div className="mt-2 h-3 w-3/4 rounded bg-muted" /></div>;
}